import { gql, useMutation } from '@apollo/client';

// Fragment for shared Pro fields
export const PRO_SHARED_FIELDS_FRAGMENT = gql`
    fragment ProFragment on Pro {
        _id
        humanId
        slug
        commercialName
        name
        email
        phoneNumber
        isGroup
        logo
        photo
        bossFirstname
        bossLastname
        bossEmail
        bossGender
        bossBirthday
        bossPhoneNumber
        bossAddress
        bossAddress2
        bossPostal
        bossCity
        bossCountry
        bossCountryCode
        address
        address2
        postal
        city
        country
        countryCode
        legal
        siret
        publicProfile
        ape
        vat
        rcs
        rm
        numberOfEmployees
        auto
        capital
        description
        experience
        active
        role
        createdDate
        totalJobOffers
        totalJobApplications
        totalBrands
        social {
            website
            instagram
            facebook
            linkedin
        }
        sector {
            _id
            sectorName
        }
        principalProfession {
            _id
            name
        }
        brandLogo {
            _id
            url
            name
            size
            mimetype
        }
        brandBanner {
            _id
            url
            name
            size
            mimetype
        }
        video {
            _id
            url
            name
            size
            mimetype
        }
        config {
            dismissalGuarantee
            resignationGuarantee
            recruitmentMethod
            additionalJobOfferQuota
            usedClassicJobOfferQuota
            usedPremiumJobOfferQuota
            classicJobOfferQuota
            premiumJobOfferQuota
            additionalCompanyQuota
            additionalAdminUsersQuota
            additionalPhotosQuota
        }
        geoLocations {
            _id
            address
            lat
            lng
            city
            postal
        }
        profileCompletionRate {
            rate
            sections {
                company { completed }
                identity { completed }
                contact { completed }
                about { completed }
                geolocation { completed }
                galleries { completed }
                video { completed }
            }
        }
    }
`;

// Fragment for ProConfig fields
export const PRO_CONFIG_FRAGMENT = gql`
    fragment ProConfigFragment on ProConfig {
        dismissalGuarantee
        resignationGuarantee
        recruitmentMethod
        additionalJobOfferQuota
        usedClassicJobOfferQuota
        usedPremiumJobOfferQuota
        classicJobOfferQuota
        premiumJobOfferQuota
        additionalCompanyQuota
        additionalAdminUsersQuota
        additionalPhotosQuota
    }
`;

export const FEATURE_FRAGMENT = gql`
    fragment FeatureFragment on Feature {
        _id
        key
        name
        planName
        validUntil
        description
        configuration {
            duration
            unit
            enabled
            maximum
            limit
            used
            type
        }
    }
`;

// ==================== QUERIES ====================

export const GET_PRO = gql`
    query getPro($proId: ID!) {
        getPro(proId: $proId) {
            ...ProFragment
            openHours {
                day
                date
                openHourStart
                openHourEnd
                breakHourStart
                breakHourEnd
            }
            autoAcceptBooking
            professions {
                _id
                name
            }
            specialties {
                _id
                name
            }
            bankAccounts {
                last4Number
                bankName
                bank
                bic
                iban
                stripeId
            }
            brandPhotos {
                _id
                url
                name
                size
                mimetype
            }
            brandVideos {
                _id
                url
                name
                size
                mimetype
            }
            salaryStats {
                lowest
                highest
            }
            features {
                ...FeatureFragment
            }
            credits {
                ...FeatureFragment
            }
            planCredits {
                key
                credits {
                    ...FeatureFragment
                }
            }
            onboardingCompleteDate
        }
    }
    ${PRO_SHARED_FIELDS_FRAGMENT}
    ${FEATURE_FRAGMENT}
`;

export const GET_PROS = gql`
    query getPros($query: String, $pagination: Pagination, $sortBy: ProSortBy, $sortOrder: Int, $professionId: ID, $filter: ProFilter) {
        getPros(query: $query, pagination: $pagination, sortBy: $sortBy, sortOrder: $sortOrder, professionId: $professionId, filter: $filter) {
            docs {
                _id
                commercialName
                name
                email
                logo
                photo
                bossFirstname
                bossLastname
                bossEmail
                address
                address2
                postal
                city
                country
                active
                role
                createdDate
                totalJobOffers
                totalJobApplications
                totalBrands
                isFeatured
                userSubscriptionPlans {
                    name
                    status
                }
                sector {
                    _id
                    sectorName
                }
                principalProfession {
                    _id
                    name
                }
                brandLogo {
                    _id
                    url
                }
            }
            meta {
                totalDocs
                limit
                page
                totalPages
                hasNextPage
                hasPrevPage
            }
        }
    }
`;

export const GET_ALL_PROS = gql`
    query getAllPros {
        getAllPros {
            _id
            commercialName
            email
        }
    }
`;

export const SEARCH_COMPANIES_FILTERS = gql`
    query searchProFilters($query: String, $filter: ProFilter) {
        searchProFilters(query: $query, filter: $filter) {
            sectors {
                value
                label
                count
            }
            postalCodes {
                value
                label
                count
            }
            cities {
                value
                label
                count
            }
            totalJobOffersRange {
                min
                max
            }
        }
    }
`;

export const GET_PRO_PUBLIC = gql`
    query getProPublic($slug: String!) {
        getProPublic(slug: $slug) {
            ...ProFragment
            openHours {
                day
                date
                openHourStart
                openHourEnd
                breakHourStart
                breakHourEnd
            }
            professions {
                _id
                name
            }
            specialties {
                _id
                name
            }
            brandPhotos {
                _id
                url
                name
                size
                mimetype
            }
            brandVideos {
                _id
                url
                name
                size
                mimetype
            }
        }
    }
    ${PRO_SHARED_FIELDS_FRAGMENT}
`;

export const GET_SELF_PRO = gql`
    query getSelfPro {
        getSelfPro {
            ...ProFragment
            openHours {
                day
                date
                openHourStart
                openHourEnd
                breakHourStart
                breakHourEnd
            }
            autoAcceptBooking
            professions {
                _id
                name
            }
            specialties {
                _id
                name
            }
            bankAccounts {
                last4Number
                bankName
                bank
                bic
                iban
                stripeId
            }
            brandPhotos {
                _id
                url
                name
                size
                mimetype
            }
            brandVideos {
                _id
                url
                name
                size
                mimetype
            }
        }
    }
    ${PRO_SHARED_FIELDS_FRAGMENT}
`;

export const GET_PRO_SEARCH = gql`
    query getProSearch($query: String, $type: String, $page: Int, $limit: Int) {
        getProSearch(query: $query, type: $type, page: $page, limit: $limit) {
            _id
            auto
            name
            commercialName
            email
            totalJobOffers
            totalJobApplications
            totalBrands
            role
            address
            postal
            city
            country
            experience
            photo
            photos
            openHours {
                day
                date
                openHourStart
                openHourEnd
                breakHourStart
                breakHourEnd
            }
            specialties {
                _id
                name
            }
            professions {
                _id
                name
            }
        }
    }
`;

export const GET_PROS_PUBLIC = gql`
    query getProsPublic($filter: ProPublicFilter, $pagination: Pagination) {
        getProsPublic(filter: $filter, pagination: $pagination) {
            docs {
                _id
                commercialName
                name
                description
                city
                address
                postal
                country
                role
                jobOfferCount
                slug
                onboardingCompleteDate
                createdDate
                brandLogo {
                    _id
                    url
                    name
                    size
                    mimetype
                }
                brandBanner {
                    _id
                    url
                    name
                    size
                    mimetype
                }
                brandPhotos {
                    _id
                    url
                    name
                    size
                    mimetype
                }
                sector {
                    _id
                    sectorName
                }
                principalProfession {
                    _id
                    name
                }
                social {
                    website
                    instagram
                    facebook
                    linkedin
                }
            }
            meta {
                totalDocs
                limit
                page
                totalPages
                hasNextPage
                hasPrevPage
            }
        }
    }
`;

// ==================== MUTATIONS ====================

export const DEACTIVATE_PRO = gql`
    mutation deactivatePro($proId: ID) {
        deactivatePro(proId: $proId) {
            deactivatedDate
            deactivateReason
        }
    }
`;

export const DELETE_PRO = gql`
    mutation deletePro($proId: ID, $reason: String!) {
        deletePro(proId: $proId, reason: $reason) {
            deletedDate
            deleteReason
        }
    }
`;

export const DELETE_PRO_ACCOUNT_UPERCUT = gql`
    mutation deleteProAccountUpercut($proId: ID!, $reason: String!) {
        deleteProAccountUpercut(proId: $proId, reason: $reason) {
            deletedDate
            deleteReason
        }
    }
`;

export const CANCEL_DELETE_PRO = gql`
    mutation cancelDeletePro($proId: ID) {
        cancelDeletePro(proId: $proId)
    }
`;

export const ADD_PHOTO_PRO = gql`
    mutation addPhotoPro($proId: ID, $file: Upload!) {
        addPhotoPro(proId: $proId, file: $file)
    }
`;

export const ADD_PRO = gql`
    mutation addPro($proInput: InputAddPro!) {
        addPro(proInput: $proInput) {
            _id
            email
            role
            active
        }
    }
`;

export const ACTIVATE_PRO = gql`
    mutation activatePro($email: String!, $code: String!) {
        activatePro(email: $email, code: $code) {
            userId
            token
            role
        }
    }
`;

export const RESEND_ACTIVATION_CODE_PRO = gql`
    mutation resendActivationCodePro($proId: ID, $talentId: ID) {
        resendActivationCodePro(proId: $proId, talentId: $talentId) {
            _id
            email
            activationCode
        }
    }
`;

export const UPDATE_PASSWORD_PRO = gql`
    mutation updatePasswordPro(
        $oldPassword: String!
        $newPassword: String!
        $newPasswordConfirmation: String!
        $userId: ID
    ) {
        updatePasswordPro(
            oldPassword: $oldPassword
            newPassword: $newPassword
            newPasswordConfirmation: $newPasswordConfirmation
            userId: $userId
        ) {
            userId
            token
            role
        }
    }
`;

export const UPDATE_EMAIL_PRO = gql`
    mutation updateEmailPro($proInput: InputUpdateEmail!, $proId: ID) {
        updateEmailPro(proInput: $proInput, proId: $proId) {
            _id
            email
        }
    }
`;

export const UPDATE_PRO_ADMINISTRATIVE_PRO = gql`
    mutation updateProAdministrativePro($proInput: InputUpdateAdministrative!, $proId: ID) {
        updateProAdministrativePro(proInput: $proInput, proId: $proId) {
            ...ProFragment
        }
    }
    ${PRO_SHARED_FIELDS_FRAGMENT}
`;

export const useUpdateProAdministrative = (options?) => {
    return useMutation(UPDATE_PRO_ADMINISTRATIVE_PRO, options);
};

export const UPDATE_BOSS_ADMINISTRATIVE_PRO = gql`
    mutation updateBossAdministrativePro($proInput: InputUpdateAdministrativeBoss!, $proId: ID) {
        updateBossAdministrativePro(proInput: $proInput, proId: $proId) {
            _id
            bossFirstname
            bossLastname
            bossBirthday
            bossEmail
            bossGender
            bossAddress
            bossAddress2
            bossPostal
            bossPhoneNumber
            bossCity
            bossCountry
            bossCountryCode
        }
    }
`;

export const UPDATE_SOCIAL_PRO = gql`
    mutation updateSocialPro($proInput: InputUpdateSocial!, $proId: ID) {
        updateSocialPro(proInput: $proInput, proId: $proId) {
            _id
            social {
                website
                instagram
                facebook
                linkedin
            }
        }
    }
`;

export const UPDATE_OPEN_HOURS_PRO = gql`
    mutation updateOpenHoursPro($openHours: [InputOpenHour]!, $proId: ID) {
        updateOpenHoursPro(openHours: $openHours, proId: $proId) {
            day
            date
            openHourStart
            openHourEnd
            breakHourStart
            breakHourEnd
        }
    }
`;

export const UPDATE_AUTO_ACCEPT_BOOKING_PRO = gql`
    mutation updateAuotAcceptBookingPro($value: Boolean, $proId: ID) {
        updateAuotAcceptBookingPro(value: $value, proId: $proId)
    }
`;

export const UPDATE_PHOTO = gql`
    mutation updatePhoto($userId: ID, $file: Upload!) {
        updatePhoto(userId: $userId, file: $file)
    }
`;

export const ADD_VIDEO_PRO = gql`
    mutation addVideoPro($video: UploadFile!) {
        addVideoPro(video: $video) {
            _id
            video {
                _id
                url
                name
                size
                mimetype
            }
            brandVideos {
                _id
                url
                name
                size
                mimetype
            }
        }
    }
`;

export const UPDATE_PRO_BRAND = gql`
    mutation updateProBrand($proId: ID!, $brandInput: ProBrandInput!) {
        updateProBrand(proId: $proId, brandInput: $brandInput) {
            _id
            brandLogo {
                _id
                url
                name
                size
                mimetype
            }
            brandBanner {
                _id
                url
                name
                size
                mimetype
            }
            brandPhotos {
                _id
                url
                name
                size
                mimetype
            }
            brandVideos {
                _id
                url
                name
                size
                mimetype
            }
        }
    }
`;

export const SEND_DATA_FILE_PRO = gql`
    mutation sendDataFilePro($proId: ID!) {
        sendDataFilePro(proId: $proId)
    }
`;

export const UPDATE_DESCRIPTION_PRO = gql`
    mutation updateDescriptionPro($experience: Date, $description: String, $proId: ID!, $numberOfEmployees: Int) {
        updateDescriptionPro(experience: $experience, description: $description, proId: $proId, numberOfEmployees: $numberOfEmployees)
    }
`;

export const UPDATE_ONBOARDING_COMPLETE_PRO = gql`
    mutation updateOnboardingCompletePro($proId: ID!) {
        updateOnboardingCompletePro(proId: $proId)
    }
`;

export const UPDATE_GEO_LOCATION_PRO = gql`
    mutation updateGeoLocationPro($proId: ID, $geoLocations: [InputProGeoLocation!], $shouldDelete: Boolean) {
        updateGeoLocationPro(proId: $proId, geoLocations: $geoLocations, shouldDelete: $shouldDelete) {
            _id
            geoLocations {
                _id
                address
                lat
                lng
                city
                postal
            }
        }
    }
`;

export const UPDATE_PROS_FEATURED = gql`
    mutation updateProsFeatured($ids: [ID!]!, $isFeatured: Boolean!) {
        updateProsFeatured(_ids: $ids, isFeatured: $isFeatured) {
            _id
            isFeatured
        }
    }
`;

export const UPDATE_CONFIG_UPERCUT = gql`
    mutation updateConfigUpercut($proId: ID, $config: InputProConfig!) {
        updateConfigUpercut(proId: $proId, config: $config) {
            _id
            config {
                dismissalGuarantee
                resignationGuarantee
                recruitmentMethod
                additionalJobOfferQuota
                usedClassicJobOfferQuota
                usedPremiumJobOfferQuota
                classicJobOfferQuota
                premiumJobOfferQuota
                additionalCompanyQuota
                additionalAdminUsersQuota
                additionalPhotosQuota
            }
        }
    }
`;

export const GET_FROM_SIRET_PAPPERS = gql`
    query getFromSiretPappers($siret: String!) {
        getFromSiretPappers(siret: $siret) {
            pro {
                siret
                name
                legal
                commercialName
                ape
                address
                postal
                city
                country
                bossFirstname
                bossLastname
                bossBirthday
            }
            isClaimed
            isAllowed
        }
    }
`;

export const ADD_PRO_UPERCUT = gql`
    mutation addProUpercut($proInput: InputAddProUpercut!) {
        addProUpercut(proInput: $proInput) {
            _id
            email
            siret
            role
            active
        }
    }
`;

export const SEND_ACTIVATION_EMAIL_PRO = gql`
    mutation sendActivationEmailPro($proId: ID!) {
        sendActivationEmailPro(proId: $proId) {
            _id
            email
        }
    }
`;
